import { Link } from 'react-router-dom'
import Navbar from '../components/Navbar'
import SEO from '../components/SEO'

const SECTIONS = [
  { id: 'ratings',    label: 'Content ratings' },
  { id: 'allowed',    label: "What's allowed" },
  { id: 'groups',     label: 'Groups' },
  { id: 'prohibited', label: "What's not allowed" },
  { id: 'fanfic',     label: 'Fan fiction' },
  { id: 'accounts',   label: 'Accounts & age' },
  { id: 'enforcement',label: 'Enforcement' },
]

export default function GuidelinesPage() {
  return (
    <div className="legal-page">
      <SEO title="Content Guidelines" description="Verse content guidelines — ratings, what's allowed, fanfiction rules, and enforcement policies." />
      <Navbar />
      <div className="legal-container">

        <div className="legal-hero">
          <h1 className="legal-hero__title">Content Guidelines</h1>
          <p className="legal-hero__sub">
            Verse is a creative space for writers and readers of all kinds. These guidelines exist to keep it safe,
            welcoming, and legal for everyone. If you write on Verse, please read them.
          </p>
          <p style={{ fontSize: '0.82rem', color: 'var(--text-faint)', marginTop: 8 }}>
            Last updated: March 2026 · For legal details see our <Link to="/terms" className="legal-link">Terms of Service</Link>
          </p>
        </div>

        <div className="legal-layout">
          {/* TOC */}
          <nav className="legal-toc">
            <div className="legal-toc__title">On this page</div>
            <ul className="legal-toc__list">
              {SECTIONS.map(s => (
                <li key={s.id}>
                  <a href={`#${s.id}`} className="legal-toc__link">{s.label}</a>
                </li>
              ))}
            </ul>
          </nav>

          {/* Content */}
          <div className="legal-body">

            {/* Ratings */}
            <section id="ratings" className="legal-section">
              <h2 className="legal-h2">Content ratings</h2>
              <p>Every story must have a rating. You're responsible for rating your work accurately — mislabeling to bypass restrictions is a violation.</p>

              <div className="guidelines-rating-grid">
                <div className="guidelines-rating-card guidelines-rating-card--teen">
                  <div className="guidelines-rating-card__badge">Teen</div>
                  <p className="guidelines-rating-card__desc">Suitable for readers 13+. May include moderate themes but nothing explicitly adult.</p>
                  <ul className="guidelines-rating-card__list">
                    <li>Mild violence (no graphic gore)</li>
                    <li>Romance, kissing, and mild sexual references (no graphic or explicit sexual content)</li>
                    <li>Moderate language</li>
                    <li>Dark or emotionally heavy themes</li>
                  </ul>
                </div>

                <div className="guidelines-rating-card guidelines-rating-card--mature">
                  <div className="guidelines-rating-card__badge">Mature</div>
                  <p className="guidelines-rating-card__desc">For adult readers only (18+). Teen accounts cannot access mature content.</p>
                  <ul className="guidelines-rating-card__list">
                    <li>Sexual themes (implied intimacy, fading to black)</li>
                    <li>Sexually explicit content (graphic depictions — must be flagged; disables tipping)</li>
                    <li>Graphic violence</li>
                    <li>Strong language</li>
                    <li>Heavy or disturbing themes</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Allowed */}
            <section id="allowed" className="legal-section">
              <h2 className="legal-h2">What's allowed</h2>
              <p>Verse is built for creative writing in all its forms. You can post:</p>
              <ul>
                <li><strong>Original fiction</strong> — any genre, any length</li>
                <li><strong>Fan fiction</strong> — stories based on existing works (see fan fiction section below)</li>
                <li><strong>Poetry and prose</strong> — experimental forms welcome</li>
                <li><strong>Dark themes</strong> — grief, trauma, moral complexity, and difficult subject matter are valid literary territory when handled thoughtfully and rated appropriately</li>
                <li><strong>Mature content</strong> — explicit content between adult characters, correctly rated as Mature</li>
                <li><strong>LGBTQ+ content</strong> — all identities and relationships are welcome on Verse</li>
              </ul>
            </section>

            {/* Groups */}
            <section id="groups" className="legal-section">
              <h2 className="legal-h2">Groups</h2>
              <p>Groups are community spaces open to all users, including minors. Because of this, all group content must be appropriate for a general audience:</p>
              <ul>
                <li><strong>No sexually explicit content</strong> — including adult roleplay, explicit stories, or graphic sexual descriptions</li>
                <li><strong>No graphic violence or gore</strong> — beyond what would appear in a Teen-rated story</li>
                <li><strong>No mature themes without appropriate framing</strong> — if it would require a Mature rating as a story, it doesn't belong in a group post</li>
              </ul>
              <p>If you want to write mature or explicit content, publish it as a properly rated story — that's what the rating system is for. Groups are for discussion, community, and general-audience interaction.</p>
            </section>

            {/* Prohibited */}
            <section id="prohibited" className="legal-section">
              <h2 className="legal-h2">What's not allowed</h2>
              <p>The following content is not allowed anywhere on Verse. If any part of a story contains prohibited content, the entire story will be removed. Violations may also result in a permanent ban.</p>

              <div className="guidelines-rule">
                <div className="guidelines-rule__label" style={{ background: '#7C3AED', color: '#fff' }}>Allowed under Mature rating</div>
                <ul>
                  <li><strong>Violence/gore that serves the plot</strong> — war stories, horror, crime thrillers</li>
                  <li><strong>Sexual content that's part of the narrative</strong> — romance, character relationships</li>
                  <li><strong>Dark themes explored meaningfully</strong> — abuse, addiction, trauma handled with craft and purpose</li>
                </ul>
              </div>

              <div className="guidelines-rule">
                <div className="guidelines-rule__label guidelines-rule__label--hard">Not allowed regardless of rating</div>
                <ul>
                  <li><strong>Gratuitous content with no narrative purpose</strong> — content that exists only for shock value</li>
                  <li><strong>Content involving minors in sexual situations</strong> — never allowed, period. Fictional or otherwise.</li>
                  <li><strong>Content that glorifies or instructs real-world violence</strong> — depicting violence in fiction is fine; encouraging or instructing it is not</li>
                  <li><strong>CSAM</strong> — we are legally required to report this to the National Center for Missing and Exploited Children (NCMEC) and law enforcement. Immediate permanent ban, no exceptions.</li>
                </ul>
              </div>

              <div className="guidelines-rule">
                <div className="guidelines-rule__label guidelines-rule__label--hard">Also prohibited</div>
                <ul>
                  <li>Doxxing or sharing private information about real people without consent</li>
                  <li>Harassment campaigns targeting specific individuals</li>
                  <li>Content designed to incite real-world violence against a specific person or group</li>
                  <li>Spam — mass-posted low-quality content, advertising, or AI-generated filler</li>
                  <li>Malware, phishing links, or deceptive content</li>
                  <li>Deliberately mislabelled ratings to expose younger readers to adult content</li>
                </ul>
              </div>

              <p>
                Dark themes in fiction — violence, abuse, trauma — are allowed when handled with craft and correctly rated.
                The line is between exploring difficult subjects thoughtfully and content that exists only to harm or exploit.
              </p>
            </section>

            {/* Fan fiction */}
            <section id="fanfic" className="legal-section">
              <h2 className="legal-h2">Fan fiction</h2>
              <p>
                Fan fiction is at the heart of Verse. We support transformative works and believe fan creativity
                deserves a home. Here's how to do it right:
              </p>
              <ul>
                <li><strong>Tag your source.</strong> Always include the original fandom in your story's fandom tag so readers know what it's based on.</li>
                <li><strong>Non-commercial only.</strong> Fan fiction of copyrighted works must be free. Don't charge readers to access it.</li>
                <li><strong>Transformative, not reproductive.</strong> Fan fiction should reimagine, not copy. Don't paste large sections of the original work into your story.</li>
                <li><strong>Respect author wishes.</strong> Some creators have publicly asked that fan fiction not be written about their work. We ask that you honour those requests.</li>
                <li><strong>Real person fiction (RPF).</strong> Fiction about real public figures (actors, musicians, athletes) is allowed but must be clearly fictional, not defamatory, and must not sexualise real minors.</li>
              </ul>
              <p>
                If your work receives a DMCA takedown notice, see our <Link to="/dmca" className="legal-link">DMCA policy</Link> for how to respond, including how to file a counter-notice if you believe the takedown was incorrect.
              </p>
            </section>

            {/* Accounts */}
            <section id="accounts" className="legal-section">
              <h2 className="legal-h2">Accounts & age</h2>
              <ul>
                <li><strong>Minimum age is 13.</strong> If we discover an account belongs to someone under 13, it will be deleted immediately.</li>
                <li><strong>Teen accounts (13–17)</strong> are automatically restricted from seeing Mature-rated content. This cannot be bypassed.</li>
                <li><strong>One account per person.</strong> Creating additional accounts to evade a ban is a violation.</li>
                <li><strong>You are responsible for your account.</strong> Don't share your login. Any content posted from your account is your responsibility.</li>
              </ul>
            </section>

            {/* Enforcement */}
            <section id="enforcement" className="legal-section">
              <h2 className="legal-h2">Enforcement</h2>
              <p>When content or accounts violate these guidelines, we may take the following actions depending on severity:</p>
              <ul>
                <li><strong>Content removal</strong> — the specific post, story, or comment is removed</li>
                <li><strong>Group post removal</strong> — inappropriate group content is removed with a warning; repeat violations may result in removal from the group or account suspension</li>
                <li><strong>Warning</strong> — a notice sent to your account for minor or first violations</li>
                <li><strong>Temporary suspension</strong> — account access restricted for a period of time</li>
                <li><strong>Permanent ban</strong> — for severe or repeat violations</li>
              </ul>
              <p>
                If you believe your content was removed or your account was actioned in error, contact us at{' '}
                <a href="mailto:support@versesite.com" className="legal-link">support@versesite.com</a> and we'll review it.
              </p>
              <p>
                To report content that violates these guidelines, use the report button on any story, comment, or profile.
              </p>
            </section>

          </div>
        </div>
      </div>
    </div>
  )
}
